import * as React from "react";
import ReactPlayer from "react-player";
import { Icon } from "semantic-ui-react";
import { INITIAL_STATE, reducer, PlayerAction } from "./player_reducer";
import { PlayerState, OverlayState } from "./player_state";
import PlayerOverlay from "./player_overlay";
import PlayerControls from "./player_controls";
import { UserCourseModuleOutputDto } from "../../../../models/course/learning_module";
import ExpandableSegment from "../../../../app/components/expandable_segment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MediaTrackingEvents } from "../../../../models/user/user_lesson_action";

interface LessonVideoPlayerProps {
  module?: UserCourseModuleOutputDto;
  index: number;
  videoKey: string;
  activeVideoIndex: number;
  setActiveVideoIndex: (index: number) => void;
  saveMediaTrackingEvents?: (events: MediaTrackingEvents) => void;
}

const SEEK_GRACE_SECONDS = 3;
const COMPLETION_THRESHOLD = 0.95;

function usePageActive() {
  const [active, setActive] = useState(
    document.visibilityState === "visible" && document.hasFocus(),
  );

  useEffect(() => {
    const checkActive = () => {
      setActive(document.visibilityState === "visible" && document.hasFocus());
    };

    document.addEventListener("visibilitychange", checkActive);
    window.addEventListener("focus", checkActive);
    window.addEventListener("blur", checkActive);

    checkActive();

    return () => {
      document.removeEventListener("visibilitychange", checkActive);
      window.removeEventListener("focus", checkActive);
      window.removeEventListener("blur", checkActive);
    };
  }, []);

  return active;
}

const LessonVideoPlayer: React.FC<LessonVideoPlayerProps> = ({
  module,
  index,
  videoKey,
  activeVideoIndex,
  setActiveVideoIndex,
  saveMediaTrackingEvents,
}) => {
  const allowForwardSeek = false;
  const getInitialSeekPosition = () => {
    if (module?.userModuleQuiz?.completedFlag) return 0;

    return index === 0
      ? (module?.userModuleQuiz?.mediaTrackingEventsJson?.v1LastSeekPos ?? 0)
      : (module?.userModuleQuiz?.mediaTrackingEventsJson?.v2LastSeekPos ?? 0);
  };

  const initialSeekPosition = getInitialSeekPosition();

  const [isPlaying, setIsPlaying] = useState(false);
  const [lastSeekPos, setLastSeekPos] = useState<number>(initialSeekPosition);
  const [hasSeeked, setHasSeeked] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);

  const [state, dispatch] = React.useReducer<
    React.Reducer<PlayerState, PlayerAction>
  >(reducer, INITIAL_STATE);

  const playerRef = useRef<ReactPlayer>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const currentTimeRef = useRef<number>(initialSeekPosition);
  const lastAllowedTimeRef = useRef<number>(initialSeekPosition);
  const isSeekingBackRef = useRef(false);

  const playerInstanceIdRef = useRef(crypto.randomUUID());
  const LOCK_KEY = "lesson-video-lock";

  const isPageActive = usePageActive();

  const updateInterval = useMemo(() => {
    const minutes = parseInt(
      process.env.REACT_APP_MEDIA_TRACK_INTERVAL_IN_MIN?.toString() ?? "2",
      10,
    );

    return Number.isFinite(minutes) && minutes > 0
      ? minutes * 60 * 1000
      : 2 * 60 * 1000;
  }, []);

  const initializeUserModuleQuiz = (moduleToInit: any) => {
    if (!moduleToInit.userModuleQuiz) {
      moduleToInit.userModuleQuiz = {
        id: 0,
        accountId: 0,
        locationId: 0,
        courseId: 0,
        moduleId: 0,
        userId: 0,
      };
    }

    if (!moduleToInit.userModuleQuiz.mediaTrackingEventsJson) {
      moduleToInit.userModuleQuiz.mediaTrackingEventsJson = {};
    }
  };

  const saveTrackingData = useCallback(
    (seekTime?: number) => {
      if (!module || !saveMediaTrackingEvents) return;

      const time = seekTime ?? currentTimeRef.current ?? 0;

      initializeUserModuleQuiz(module);

      const tracking = module.userModuleQuiz.mediaTrackingEventsJson ?? {};
      module.userModuleQuiz.mediaTrackingEventsJson = tracking;

      if (index === 0) {
        tracking.v1LastSeekPos = time;
        tracking.v1TotalViewTime = time;
      } else if (index === 1) {
        tracking.v2LastSeekPos = time;
        tracking.v2TotalViewTime = time;
      }

      saveMediaTrackingEvents(tracking);
      setLastSeekPos(time);
    },
    [module, saveMediaTrackingEvents, index],
  );

  useEffect(() => {
    if (activeVideoIndex === index && isPlaying) {
      dispatch({ type: "PLAY" });
    } else {
      dispatch({ type: "PAUSE" });
    }
  }, [activeVideoIndex, index, isPlaying]);

  const acquireLock = () => {
    localStorage.setItem(
      LOCK_KEY,
      JSON.stringify({
        instanceId: playerInstanceIdRef.current,
        moduleId: module?.module?.moduleId,
        videoIndex: index,
        updatedAt: Date.now(),
      }),
    );
  };

 const releaseLock = useCallback(() => {
  const raw = localStorage.getItem(LOCK_KEY);
  if (!raw) return;

  try {
    const lock = JSON.parse(raw);

    if (lock.instanceId === playerInstanceIdRef.current) {
      localStorage.removeItem(LOCK_KEY);
    }
  } catch {
    localStorage.removeItem(LOCK_KEY);
  }
}, []);

  const handlePreview = () => {
    const startTime = currentTimeRef.current > 0 ? currentTimeRef.current : 1;

    dispatch({ type: "PLAY" });
    dispatch({ type: "LIGHT", payload: false });

    setHasPlayed(true);

    currentTimeRef.current = startTime;
    lastAllowedTimeRef.current = Math.max(
      lastAllowedTimeRef.current,
      startTime,
    );

    saveTrackingData(startTime);
  };

  const handlePause = useCallback(() => {
    releaseLock();
    dispatch({ type: "PAUSE" });
    setIsPlaying(false);
  }, [releaseLock]);

  const handlePlay = () => {
    acquireLock();

    setActiveVideoIndex(index);
    dispatch({ type: "PLAY" });
    setIsPlaying(true);
    setHasPlayed(true);
  };

  const handleProgress = (progress: { playedSeconds: number }) => {
    const newTime = progress.playedSeconds;
    const lastAllowedTime = lastAllowedTimeRef.current;

    if (isSeekingBackRef.current) {
      isSeekingBackRef.current = false;
      return;
    }

    if (newTime > lastAllowedTime + SEEK_GRACE_SECONDS) {
      isSeekingBackRef.current = true;
      playerRef.current?.seekTo(lastAllowedTime, "seconds");
      return;
    }

    currentTimeRef.current = newTime;
    lastAllowedTimeRef.current = Math.max(lastAllowedTimeRef.current, newTime);

    dispatch({ type: "SEEK", payload: newTime });

    const timeDifference = newTime - lastSeekPos;
    if (timeDifference * 1000 >= updateInterval) {
      saveTrackingData(newTime);
    }
  };

  const handleEnded = () => {
    dispatch({ type: "LIGHT", payload: true });
    dispatch({ type: "PAUSE" });

    setIsPlaying(false);
    setHasSeeked(true);
    playerRef.current?.showPreview();

    if (!module) return;

    initializeUserModuleQuiz(module);

    const duration = playerRef.current?.getDuration?.() ?? 0;
    const lastAllowedTime = lastAllowedTimeRef.current;

    const isValidCompletion =
      duration > 0 && lastAllowedTime >= duration * COMPLETION_THRESHOLD;

    const watchedSeconds = isValidCompletion ? duration : lastAllowedTime;

    const tracking = module.userModuleQuiz.mediaTrackingEventsJson ?? {};
    module.userModuleQuiz.mediaTrackingEventsJson = tracking;

    if (index === 0) {
      tracking.v1LastSeekPos = watchedSeconds;
      tracking.v1TotalViewTime = watchedSeconds;
      tracking.v1SeekedToEnd = isValidCompletion;

      if (isValidCompletion) {
        tracking.v1CompletedDate = new Date();
      }
    } else if (index === 1) {
      tracking.v2LastSeekPos = watchedSeconds;
      tracking.v2TotalViewTime = watchedSeconds;
      tracking.v2SeekedToEnd = isValidCompletion;

      if (isValidCompletion) {
        tracking.v2CompletedDate = new Date();
      }
    }

    currentTimeRef.current = watchedSeconds;
    lastAllowedTimeRef.current = watchedSeconds;
    setLastSeekPos(watchedSeconds);

    saveMediaTrackingEvents?.(tracking);
  };

  const handleDuration = (duration: number) => {
    dispatch({ type: "DURATION", payload: duration });
  };

  const handleSeek = (seconds: number) => {
    const lastAllowedTime = lastAllowedTimeRef.current;

    if (seconds > lastAllowedTime + SEEK_GRACE_SECONDS) {
      isSeekingBackRef.current = true;
      playerRef.current?.seekTo(lastAllowedTime, "seconds");
      return;
    }

    currentTimeRef.current = seconds;
    lastAllowedTimeRef.current = Math.max(lastAllowedTimeRef.current, seconds);

    dispatch({ type: "SEEK", payload: seconds });
  };

  const handleReady = () => {
    if (!playerRef.current || hasSeeked) return;

    let seekPosition =
      index === 0
        ? (module?.userModuleQuiz?.mediaTrackingEventsJson?.v1LastSeekPos ?? 0)
        : (module?.userModuleQuiz?.mediaTrackingEventsJson?.v2LastSeekPos ?? 0);

    if (module?.userModuleQuiz?.completedFlag) {
      seekPosition = 0;
    }

    playerRef.current.seekTo(seekPosition, "seconds");

    dispatch({ type: "PROGRESS", payload: seekPosition });

    setLastSeekPos(seekPosition);
    setHasSeeked(true);

    currentTimeRef.current = seekPosition;
    lastAllowedTimeRef.current = seekPosition;
  };

  const playerStyle: React.CSSProperties = {
    position: "relative",
    aspectRatio: "16/9",
    borderRadius: "8px",
    overflow: "hidden",
  };

  const controlsStyle: React.CSSProperties = {
    opacity: hasPlayed ? 1 : 0,
    transition: "opacity 0.2s ease-in-out",
  };

  const overlayState: OverlayState = {
    light: state.light,
    playing: state.playing,
  };

  const getVideoUrl = (videoKey: string) => {
    return module?.module?.videos?.[videoKey]?.resolutions?.[0]?.videoUrl ?? "";
  };

  const getImage = (videoIndex: number) => {
    const mediaTwoCode = module?.module.mediaTwoCode;

    return mediaTwoCode && mediaTwoCode.toString().trim() !== ""
      ? `/assets/courses/v${videoIndex + 1}_cover.png`
      : "/assets/courses/v_cover.png";
  };

  const getStatus = (videoIndex: number) => {
    if (module?.userModuleQuiz?.completedFlag) return "Completed";

    const tracking = module?.userModuleQuiz?.mediaTrackingEventsJson;
    if (!tracking) return hasPlayed ? "In Progress" : "Not Started";

    if (videoIndex === 0) {
      if (tracking.v1CompletedDate) return "Completed";
      if ((tracking.v1TotalViewTime ?? 0) > 0 || hasPlayed) {
        return "In Progress";
      }
    } else {
      if (tracking.v2CompletedDate) return "Completed";
      if ((tracking.v2TotalViewTime ?? 0) > 0 || hasPlayed) {
        return "In Progress";
      }
    }

    return "Not Started";
  };

  const status = getStatus(index);

  useEffect(() => {
    if (!isPageActive) {
      handlePause();
    }
  }, [isPageActive, handlePause]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== LOCK_KEY || !event.newValue) return;

      try {
        const lock = JSON.parse(event.newValue);

        if (lock.instanceId !== playerInstanceIdRef.current) {
          handlePause();
        }
      } catch {
        handlePause();
      }
    };

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
      releaseLock();
    };
  }, [handlePause, releaseLock]);

  return (
    <>
      {module?.module?.isAudio && (
        <div className="lesson-course">
          <br />
          This is an audio-only module. Press play to listen.
        </div>
      )}

      {!module?.module?.isAudio && index === 0 && (
        <ExpandableSegment
          title="How to play a video?"
          content={
            <ol>
              <li>
                <b>Play Videos:</b> Click on a video to start playback.
              </li>
              <li>
                <b>Pause Control:</b> Pause a video by clicking the pause icon
                or anywhere on the video.
              </li>
              <li>
                <b>Volume:</b> Change volume of the video.
              </li>
              <li>
                <b>Full Screen:</b> Double-click to enter full-screen mode, and
                double-click again to exit.
              </li>
            </ol>
          }
        />
      )}

      {!module?.module?.isAudio && (
        <h3>
          {status === "Completed" && <Icon name="check" color="green" />}
          {status === "In Progress" && <Icon name="clock" color="blue" />}
          {status === "Not Started" && (
            <Icon name="hand point right outline" color="grey" />
          )}
          Video {index + 1}: {status}
        </h3>
      )}

      <div style={playerStyle} ref={wrapperRef}>
        <ReactPlayer
          ref={playerRef}
          url={getVideoUrl(videoKey)}
          width="100%"
          height="100%"
          light={getImage(index)}
          playIcon={
            <Icon
              name="play circle"
              size="massive"
              style={{
                color: "white",
                fontSize: "6rem",
              }}
            />
          }
          controls={allowForwardSeek ? state.controls : false}
          loop={state.loop}
          muted={state.muted}
          playing={state.playing}
          playbackRate={state.playbackRate}
          volume={state.volume}
          onReady={handleReady}
          onPlay={handlePlay}
          onEnded={handleEnded}
          onPause={handlePause}
          onDuration={handleDuration}
          onProgress={handleProgress}
          onSeek={handleSeek}
          onClickPreview={handlePreview}
        />

        {!state.playing && <PlayerOverlay state={overlayState} />}

        <div style={controlsStyle}>
          <PlayerControls
            state={state}
            dispatch={dispatch}
            playerRef={playerRef}
            wrapperRef={wrapperRef}
            hasPlayed={hasPlayed}
            blockForward={typeof saveMediaTrackingEvents === "function"}
          />
        </div>
      </div>
    </>
  );
};

export default LessonVideoPlayer;